// Virtualiserad minimap: idag ritar minimapen ALLA blocks + alla connectors
// varje frame. Ovanför ~500 blocks blir det synligt spamm. Den här helpern
// beräknar vilka blocks som skall renderas och dedupe:ar connectors.
//
// Två culling-lägen:
//
//  1. virtualizeMinimap(viewport)
//     Filtrera efter en logisk-koord viewport (minX/maxX/minY/maxY). Bra för
//     stora canvases där användaren bara ser en del — men minimapen visar
//     hela canvas, så detta används främst för testfixturer.
//
//  2. bucketCullForMinimap(scale)
//     Pixelbucket-culling i minimap-koordinatrymden. När scale är så liten
//     att flera blocks faller inom samma minimap-pixel behåller vi bara ett
//     per bucket. Connectorer vars båda ändar inte överlevde droppas.
//     Detta är default-vägen för Minimap-rendering.

export interface MinimapBlock {
  id: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
}

export interface MinimapConnector {
  id: string;
  fromId: string;
  toId: string;
}

export interface MinimapViewport {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface VirtualizationOptions {
  /** Blocks utanför viewporten men inom overscan renderas också. Default 200. */
  overscan?: number;
  /** Max blocks att rendera oavsett viewport (försvar mot extrem zoom-out). */
  renderCap?: number;
  /** Trösklel (antal blocks) under vilken alla renderas ändå. Default 300. */
  minThreshold?: number;
}

export interface VirtualizedResult {
  visibleBlocks: MinimapBlock[];
  visibleConnectors: MinimapConnector[];
  stats: {
    totalBlocks: number;
    renderedBlocks: number;
    culledBlocks: number;
    cappedByLimit: boolean;
  };
}

/**
 * Filtrera blocks + connectors efter viewport + overscan. Returnerar stats
 * för debug-panel.
 */
export function virtualizeMinimap(
  blocks: MinimapBlock[],
  connectors: MinimapConnector[],
  viewport: MinimapViewport,
  opts: VirtualizationOptions = {},
): VirtualizedResult {
  const overscan = opts.overscan ?? 200;
  const minThreshold = opts.minThreshold ?? 300;
  const renderCap = opts.renderCap ?? Number.POSITIVE_INFINITY;

  // Ingen culling under threshold — minimapen är billig då.
  if (blocks.length <= minThreshold) {
    return {
      visibleBlocks: blocks,
      visibleConnectors: connectors,
      stats: {
        totalBlocks: blocks.length,
        renderedBlocks: blocks.length,
        culledBlocks: 0,
        cappedByLimit: false,
      },
    };
  }

  const minX = viewport.minX - overscan;
  const maxX = viewport.maxX + overscan;
  const minY = viewport.minY - overscan;
  const maxY = viewport.maxY + overscan;

  const visible = blocks.filter(b =>
    b.x >= minX && b.x <= maxX && b.y >= minY && b.y <= maxY,
  );

  const capped = visible.length > renderCap;
  const rendered = capped ? visible.slice(0, renderCap) : visible;
  const visibleIds = new Set(rendered.map(b => b.id));

  const visibleConnectors = connectors.filter(c =>
    visibleIds.has(c.fromId) && visibleIds.has(c.toId),
  );

  return {
    visibleBlocks: rendered,
    visibleConnectors,
    stats: {
      totalBlocks: blocks.length,
      renderedBlocks: rendered.length,
      culledBlocks: blocks.length - rendered.length,
      cappedByLimit: capped,
    },
  };
}

export interface BucketCullOptions {
  /** Trösklel (antal blocks) under vilken alla renderas. Default 300. */
  minThreshold?: number;
  /**
   * Minsta bucket-storlek i minimap-pixlar. Större värden = mer aggressiv
   * culling. Default 1 (ett block per minimap-pixel).
   */
  bucketPx?: number;
  /**
   * Block-id som ALLTID skall finnas kvar oavsett bucket-overlap (t.ex.
   * sökträffar, probe-trail, peer-cursor-anchors). Säkerställer att
   * användarens fokuspunkter aldrig culleras bort.
   */
  forceKeep?: ReadonlySet<string>;
}

export interface BucketCullResult {
  /** Set av block-id som skall renderas. */
  visibleBlockIds: Set<string>;
  /** Set av connector-id som skall renderas. */
  visibleConnectorIds: Set<string>;
  stats: {
    totalBlocks: number;
    renderedBlocks: number;
    culledBlocks: number;
    bucketPx: number;
  };
}

/**
 * Pixel-bucket culling för minimap-rendering. När minimap-skalan är så liten
 * att flera blocks faller inom samma minimap-pixel behålls bara ett per
 * bucket. Connectorer vars endpoints inte båda finns kvar droppas också.
 *
 * Deterministisk: samma input → samma output (försäkrar stabil layout
 * mellan re-renders och peer-prestanda).
 */
export function bucketCullForMinimap(
  blocks: MinimapBlock[],
  connectors: MinimapConnector[],
  scale: number,
  opts: BucketCullOptions = {},
): BucketCullResult {
  const minThreshold = opts.minThreshold ?? 300;
  const bucketPx = Math.max(1, opts.bucketPx ?? 1);
  const forceKeep = opts.forceKeep ?? new Set<string>();

  if (blocks.length <= minThreshold) {
    return {
      visibleBlockIds: new Set(blocks.map((b) => b.id)),
      visibleConnectorIds: new Set(connectors.map((c) => c.id)),
      stats: {
        totalBlocks: blocks.length,
        renderedBlocks: blocks.length,
        culledBlocks: 0,
        bucketPx,
      },
    };
  }

  const visibleBlockIds = new Set<string>();
  const buckets = new Map<string, string>();

  for (const b of blocks) {
    if (forceKeep.has(b.id)) {
      visibleBlockIds.add(b.id);
      continue;
    }
    const bx = Math.floor((b.x * scale) / bucketPx);
    const by = Math.floor((b.y * scale) / bucketPx);
    const key = `${bx}|${by}`;
    if (!buckets.has(key)) {
      buckets.set(key, b.id);
      visibleBlockIds.add(b.id);
    }
  }

  const visibleConnectorIds = new Set<string>();
  for (const c of connectors) {
    if (visibleBlockIds.has(c.fromId) && visibleBlockIds.has(c.toId)) {
      visibleConnectorIds.add(c.id);
    }
  }

  return {
    visibleBlockIds,
    visibleConnectorIds,
    stats: {
      totalBlocks: blocks.length,
      renderedBlocks: visibleBlockIds.size,
      culledBlocks: blocks.length - visibleBlockIds.size,
      bucketPx,
    },
  };
}
